import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { Shield, Lock, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API_URL } from '../config';
import '../styles/Auth.css';


const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState('verifying'); // verifying, form, loading, success, error
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API_URL}/api/auth/verify-reset-token/${token}`);
      setStatus('form');
    } catch (err) {
      console.error('Token verification failed:', err);
      setError(err.response?.data?.detail || 'This reset link is invalid or has expired.');
      setStatus('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setStatus('loading');
    
    try {
      await axios.post(`${API_URL}/api/auth/reset-password/${token}`, {
        token,
        password
      });
      setStatus('success');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Password reset failed:', err);
      setError(err.response?.data?.detail || 'Password reset failed. Please try again.');
      setStatus('form');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="auth-status-container">
            <Loader2 className="auth-spinner" size={48} />
            <h2>Verifying reset link...</h2>
            <p>Please wait a moment.</p>
          </div>
        );
      
      case 'form':
      case 'loading':
        return (
          <div className="auth-form-container">
            <div className="auth-header">
              <Lock size={32} className="auth-icon" />
              <h2>Set New Password</h2>
              <p>Enter your new password below.</p>
            </div>
            
            <form onSubmit={handleSubmit} className="auth-form">
              {error && (
                <div className="auth-error">
                  <AlertCircle size={16} />
                  <span>{error}</span>
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="password">New Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  data-testid="password-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              
              <button 
                type="submit" 
                className="auth-submit-btn"
                disabled={status === 'loading'}
                data-testid="reset-password-btn"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="btn-spinner" size={18} />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          </div>
        );
      
      case 'success':
        return (
          <div className="auth-status-container success">
            <CheckCircle size={64} className="success-icon" />
            <h2>Password Reset!</h2>
            <p>Your password has been reset successfully.</p>
            <p className="auth-note">Redirecting to login...</p>
            <div className="auth-actions" style={{ marginTop: '24px' }}>
              <Link to="/login" className="auth-submit-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
                Go to Login
              </Link>
            </div>
          </div>
        );
      
      case 'error':
        return (
          <div className="auth-status-container error">
            <AlertCircle size={64} className="error-icon" />
            <h2>Reset Link Invalid</h2>
            <p>{error}</p>
            <div className="auth-error-actions">
              <Link to="/forgot-password" className="auth-secondary-btn">
                Request New Reset Link
              </Link>
              <Link to="/login" className="auth-link-btn">
                Go to Login
              </Link>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <Header />
      <div className="auth-page reset-password-page">
        <div className="auth-container">
          <div className="auth-logo-container">
            <Shield size={40} />
            <span>All Secured</span>
          </div>
          
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
